import { type ReactNode } from 'react';
import { Box, Flex } from '@chakra-ui/react';
import { KText } from '@/ui-library/components';
import { HtmlRenderer } from '@/components/ats';
import { RequisitionDetailsList } from './RequisitionDetailsList';

type CustomField = {
  label?: string;
  value?: unknown;
  position?: number;
};

type DetailEntry = {
  label: ReactNode;
  value: ReactNode;
  stack?: boolean;
};

const isRichTextHtml = (value: string): boolean =>
  /<[a-z][\s\S]*>/i.test(value || '');

// Core pre-renders values: plain text, or escaped HTML with anchors when the value held a
// URL. Rendering those as markup keeps links clickable rather than showing literal tags.
const toDetailEntry = ({ label, value }: CustomField): DetailEntry => {
  const stringValue = String(value);

  if (isRichTextHtml(stringValue)) {
    return {
      label,
      value: (
        <HtmlRenderer
          htmlString={stringValue}
          overflowX="auto"
          // RequisitionDetailsList only applies KText sizing to plain strings, so a rendered
          // element has to match `sm-medium` itself or it sits larger than the rows above it.
          fontSize="sm"
          fontWeight={550}
          // HtmlRenderer only styles anchors under editorStyle; without this a link
          // inherits the body colour and reads as plain text.
          sx={{
            a: { color: 'primary-500', textDecoration: 'underline' },
            p: { fontSize: 'sm', fontWeight: 550, margin: 0 },
          }}
        />
      ),
      stack: true,
    };
  }

  return { label, value: stringValue };
};

// Handles the array format and the legacy map still emitted by older payloads.
const buildCustomFieldEntries = (
  customFields: CustomField[] | Record<string, unknown> | undefined
): DetailEntry[] => {
  if (Array.isArray(customFields)) {
    return customFields
      .filter((field) => field?.label && field?.value)
      .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
      .map(toDetailEntry);
  }

  return Object.entries(customFields || {})
    .filter(([, value]) => value)
    .map(([label, value]) => toDetailEntry({ label, value }));
};

// Requisition custom fields, under their own "Additional details" heading to match offer
// approval notifications. Renders nothing when there are no values.
export const RequisitionCustomFieldsSection = ({
  customFields,
}: {
  customFields?: CustomField[] | Record<string, unknown>;
}) => {
  const entries = buildCustomFieldEntries(customFields);

  if (entries.length === 0) return null;

  // Sits below RequisitionDetailsList in the same bordered section, so it repeats that
  // list's 16px inset.
  return (
    <Flex flexDir="column" gap="8px" p="0 16px 16px" bg="white" minW="0">
      <KText variant="heading_4" color="secondary-900" fontWeight="semiBold">
        Additional details
      </KText>
      <Box borderBottom="1px solid" borderColor="border-color" />
      <RequisitionDetailsList data={entries} p="0" />
    </Flex>
  );
};
